import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { Config } from '@forgerock/javascript-sdk';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom } from 'rxjs';
import { delay, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import {
  selectAllPartnerIds,
  selectCustomerSegmentCd,
  selectPolicySummaries,
  selectCustomerGroupId as selectSummariesCustomerGroupId
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { GenericProductType } from '@amfam/policy/models/lib/models/generic-product-type';
import {
  loadDataDog,
  loadGoogleAnalytics
} from '@amfam/shared/analytics/lib/+state/analytics.actions';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { HeaderWrapperService } from '@amfam/shared/ui/ds-header/lib/services/header-wrapper.service';
import { EditAfeSessionId } from '@amfam/shared/user/lib/+state/user.actions';
import {
  selectCustomerId,
  selectEmailAddress,
  selectUserState
} from '@amfam/shared/user/lib/+state/user.selectors';
import {
  selectCustomerGroupId,
  selectExperienceId
} from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { Partners } from '@amfam/shared/utility/brand/lib/services/brand.enum';
import { MAJOR_PARTNER_IDS } from '@amfam/shared/utility/brand/lib/services/partner-mappings';
import { selectHasFeatureFlag } from '@amfam/shared/utility/feature-flag/data-access/lib/+state/feature-flag.selectors';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { FeatureFlagPanelComponent } from '@amfam/shared/utility/feature-flag/feature/lib/feature-flag-panel/feature-flag-panel.component';
import { ContentDirective } from '@amfam/shared/utility/shared-services/lib/copy/content.directive';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { LogoutOrigin } from '@amfam/shared/utility/shared-services/lib/core/logout-origin';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { EventDispatcherService } from '@amfam/shared/utility/shared-services/lib/event-bus/event-dispatcher.service';
import { SessionEventsService } from '@amfam/shared/utility/shared-services/lib/event-bus/session-events.service';
import { MonolineService } from '@amfam/shared/utility/shared-services/lib/policy/monoline.service';
import { LOCAL_STORAGE_KEYS } from '@amfam/shared/utility/shared-services/lib/storage/storage-keys';
import { FeedbackService } from '@amfam/shared/utility/shared-services/lib/utils/feedback.service';
import { ScriptLoaderService } from '@amfam/shared/utility/shared-services/lib/utils/script-loader.service';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';
import { DockingBarService } from '@amfam/ui-kit/lib/docking-bar/services/docking-bar.service';
import { DsButtonComponent } from '@amfam/ui-kit/lib/ds-button/components/ds-button/ds-button.component';
import { DsModalComponent } from '@amfam/ui-kit/lib/ds-modal/components/ds-modal/ds-modal.component';
import { DsModalService } from '@amfam/ui-kit/lib/ds-modal/components/ds-modal/ds-modal.service';
import { LoadingSpinnerComponent } from '@amfam/ui-kit/lib/loading-spinner/components/loading-spinner.component';
import { LoadingSpinnerService } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner.service';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { AfeSessionService } from './core/auth/afe-session.service';
import { ForgerockAuthService } from './core/auth/forgerock-auth.service';
import { SalesforceChatAnalyticsService } from './core/chat/salesforce-chat-analytics.service';
import { SalesforceChatIdleService } from './core/chat/salesforce-chat-idle.service';
import {
  SalesforceChatSessionService,
  SalesforceWindow
} from './core/chat/salesforce-chat-session.service';
import { FeatureDetectionService } from './core/feature-detection/feature-detection.service';
import { selectAnalyticUserType } from './core/store';
import { SessionActivityService } from './login/session-activity/session-activity.service';
import { HeaderWrapperComponent } from './shared/header-wrapper/header-wrapper.component';

/**
 * ForgeRock SDK v4.8.0 Middleware Types
 * From: @forgerock/javascript-sdk/dist/config/interfaces.d.ts
 */
interface RequestObj {
  url: URL;
  init: RequestInit;
}

interface Action {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

type RequestMiddleware = (req: RequestObj, action: Action, next: () => RequestObj) => void;

@Component({
  selector: 'ds-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    '[class.monoline]': 'isMonoRisk()',
    '(click)': 'logSessionActivity()'
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    HeaderWrapperComponent,
    LoadingSpinnerComponent,
    DsModalComponent,
    DsButtonComponent,
    ContentDirective,
    FeatureFlagPanelComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private spinner = inject(LoadingSpinnerService);
  private sessionSvc = inject(SessionActivityService);
  private dockingService = inject(DockingBarService);
  private featureDetectionService = inject(FeatureDetectionService);
  private router = inject(Router);
  private win = inject(WindowRef);
  private store = inject(Store);
  private headerWrapperService = inject(HeaderWrapperService);
  private scriptloader = inject(ScriptLoaderService);
  private analyticsFacade = inject(AnalyticsFacade);
  private modal = inject(DsModalService);
  private eventDispatcherService = inject(EventDispatcherService);
  private dataDogService = inject(DataDogService);
  private featureService = inject(FeatureFlagService);
  private forgerockAuthService = inject(ForgerockAuthService);
  private afeSessionService = inject(AfeSessionService);
  private sessionEventsService = inject(SessionEventsService);
  private copyService = inject(CopyService);
  private document = inject(DOCUMENT);
  private utilService = inject(UtilService);
  private ngZone = inject(NgZone);
  private feedbackService = inject(FeedbackService);
  private componentViewService = inject(ComponentViewService);
  private monolineService = inject(MonolineService);
  private chatIdle = inject(SalesforceChatIdleService);
  private chatAnalytics = inject(SalesforceChatAnalyticsService);
  private chatSession = inject(SalesforceChatSessionService);
  private destroyRef = inject(DestroyRef);
  private sessionHandler: ReturnType<typeof setInterval> | undefined;

  // Service BehaviorSubjects, read through toSignal: pure, so subscribing at construction changes
  // nothing. The root stays Eager until no Eager descendants are left (an OnPush root would stop
  // refreshing them on zone ticks), so these only prepare it for that flip.
  readonly showHeaders = toSignal(this.eventDispatcherService.showHeaders$, { requireSync: true });
  // TODO fix pipe(delay(0))
  readonly noDock = toSignal(this.dockingService.dockEmpty$.pipe(delay(0)), {
    initialValue: null
  });
  readonly mobileMenuOpen = toSignal(this.headerWrapperService.mobileMenuOpen$, {
    requireSync: true
  });
  readonly mobileMenuActive = toSignal(this.headerWrapperService.mobileMenuActive$, {
    requireSync: true
  });

  logSessionActivity() {
    this.sessionSvc.registerUserActivity();
  }
  /** Preprod-only feature flag debug panel; never rendered in prod. */
  readonly showFeatureFlagPanel = env.environment !== 'prod';
  readonly noDocumentModalId = 'noDocumentModal';
  readonly noDocumentMessage = signal('');
  readonly noDocumentHeader = signal('');

  private isMonoRisk$ = this.monolineService.isMonoRiskWithLemonade$;
  isMonoRisk = toSignal(this.isMonoRisk$, { initialValue: false });

  /** Limits the teardown of a restored chat session to once per page load. */
  private chatSessionResetStarted = false;

  /**
   * ForgeRock SDK Request Middleware
   * Logs every SDK request, then delegates authorize-flow detection to ForgerockAuthService.
   * This is the only place the app can observe the SDK-owned /token/revoke call.
   * See docs/auth/forgerock-token-revoke.md
   */
  private loggingMiddleware: RequestMiddleware = (req: RequestObj, action: Action, next) => {
    this.forgerockAuthService.logSdkRequest(action.type, req.url);
    this.forgerockAuthService.handleAuthorizeFlow(req.url);
    return next();
  };

  constructor() {
    console.log(`Platform Build: ${env.version}`);

    // Detect navigation away from application (e.g., ForgeRock redirect)
    this.setupNavigationDetection();
  }

  /**
   * Sets up event listeners to detect when user is navigating away from the app
   * Useful for detecting ForgeRock login redirects
   */
  private setupNavigationDetection() {
    this.win.nativeWindow.addEventListener('beforeunload', () => {
      this.utilService.log('[Auth] Inside beforeunload');
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.setItem(LOCAL_STORAGE_KEYS.LEFT_APP, '1');
      this.sessionSvc.stopRefresh();
    });

    this.win.nativeWindow.addEventListener('pagehide', () => {
      this.utilService.log('[Auth] Inside pagehide');
    });
  }

  ngOnInit() {
    combineLatest([
      this.store.select(selectCustomerGroupId),
      this.store.select(selectAllPartnerIds),
      this.store.select(selectCustomerSegmentCd),
      this.store.select(selectEmailAddress)
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(([cId, partnerIds, segmentCode, emailAddr]) => {
          if (cId) localStorage.setItem(LOCAL_STORAGE_KEYS.CUSTOMER_GROUP_ID, cId);
          if (partnerIds?.length)
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.PARTNER_ID,
              Array.from(new Set(partnerIds || [])).join(',')
            );
          if (segmentCode)
            localStorage.setItem(LOCAL_STORAGE_KEYS.CUSTOMER_SEGMENT_CODE, segmentCode);
          if (emailAddr) localStorage.setItem(LOCAL_STORAGE_KEYS.CUSTOMER_EMAIL, emailAddr);
          // In future we will replace the Customer Group ID number from 642 to AFCG.
          // For now, we will have both available as a brand check.
          // These variables are used by customer feedback.
          if (cId && !['642', 'AFCG'].includes(cId))
            localStorage.setItem(LOCAL_STORAGE_KEYS.PARTNER_ID, cId);
        })
      )
      .subscribe();

    // Attempt to load the script only if the script URL is available in the config.json
    try {
      const scriptURL = env.links.salesForceBeconURL;
      if (scriptURL) {
        this.scriptloader.loadScript(scriptURL);
      } else {
        throw new Error('Script URL not found check app.config.ts');
      }
    } catch (error) {
      this.dataDogService.log('Error loading scriptURL', 'error', {
        error: error instanceof Error ? error.message : error
      });
      if (env.environment !== 'prod') {
        console.error('Error loading: scriptURL', error);
      }
    }

    // Load Qualtrics feedback SDK
    try {
      if (env.myAccountBuildGroupId !== Partners.LEMONADE && !this.componentViewService.inWebview) {
        // Read by Qualtrics as survey metadata (af_session_ID)
        const afSessionId = this.afeSessionService.getSessionId();
        if (afSessionId) localStorage.setItem(LOCAL_STORAGE_KEYS.AF_SESSION_ID, afSessionId);
        this.feedbackService.loadFeedbackSDK();
      }
    } catch (error) {
      this.utilService.error('Error loading feedback SDK', {
        error: error instanceof Error ? error.message : error
      });
    }

    // Load Salesforce Embedded Messaging chat widget after user authentication (if enabled)
    // Only show for customer group 642 with auto-only policies where all are partner ID 559 or 526
    combineLatest([
      this.store.select(selectCustomerId).pipe(filter(customerId => !!customerId)),
      this.store.select(selectHasFeatureFlag).pipe(filter(loaded => !!loaded)),
      this.store.select(selectCustomerGroupId).pipe(filter(cId => !!cId)),
      this.store.select(selectPolicySummaries).pipe(filter(policies => policies.length > 0))
    ])
      .pipe(
        take(1),
        tap(([_customerId, _hasFeatureFlag, customerGroupId, policies]) => {
          const salesforceChatPartnerIds: string[] = [Partners.COSTCO_ECP, Partners.COSTCO_CHOICE];
          if (
            this.featureService.isEnabled('salesforce_chat') &&
            customerGroupId === Partners.AMFAM_EXCLUSIVE &&
            policies.every(
              p =>
                salesforceChatPartnerIds.includes(p.policyPartnerId) &&
                p.generalizedProductType === GenericProductType.Auto
            )
          ) {
            this.initSalesforceChatWidget();
          }
        })
      )
      .subscribe();

    // Check the app status
    this.featureDetectionService.detect();
    this.store.dispatch(loadDataDog());
    this.store.dispatch(loadGoogleAnalytics());

    // Set AFE session ID in the store
    const afeSessionId = this.afeSessionService.getSessionId();
    if (afeSessionId) {
      this.store.dispatch(EditAfeSessionId(afeSessionId));
    }

    // TODO find a better place for this subscription / refactor analytic user type
    this.store
      .select(selectAnalyticUserType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(userType => {
        this.analyticsFacade.setProfileInfo({ userType });
      });

    const trackId = sessionStorage.getItem('trackID');
    if (trackId) datadogRum.setUser({ id: trackId });

    // Scroll to the top of the page on route change
    this.router.events.subscribe(evt => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      this.win.nativeWindow.scrollTo(0, 0);

      /*
      On navigation end, change focused element to body if needed.
      Without this the focused item will be the last item focused before the route change.
      This causes a 'focus trap' and keeps the 'Skip to content' link at the top of the page from being the first tabbed to items.
      That means that the screen reader starts at a weird place in the screen, sometimes on an element that no longer exists.
      */

      // a11y: this checks if the currently focused dom element is the body
      if (this.document.activeElement !== this.document.body) {
        // if it's not then it sets the tabindex so it can be focused
        this.document.body.tabIndex = -1;
        // then sets the focus
        this.document.body.focus();
        // then removes the tabIndex attribute again
        this.document.body.removeAttribute('tabIndex');
      }
    });

    Config.set({
      clientId: env.forgerock.webOauthClient, // e.g. 'ForgeRockSDKClient'
      redirectUri: env.forgerock.redirectUri, // Redirect back to your app, e.g. 'https://sdkapp.example.com:8443'
      scope: env.forgerock.scope, // e.g. 'openid profile email address phone me.read'
      serverConfig: {
        baseUrl: env.forgerock.baseUrl, // e.g. 'https://myorg.forgeblocks.com/am' or 'https://openam.example.com:8443/openam'
        timeout: env.forgerock.timeout // 3000 to 5000 is good, this impacts the redirect time to login
      },
      realmPath: env.forgerock.realmPath, // e.g. 'alpha' or 'root'
      oauthThreshold: 60,
      middleware: [this.loggingMiddleware]
    });

    this.eventDispatcherService.noDocumentModal$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(noDoc => {
          this.noDocumentHeader.set(
            noDoc ? 'shared.noDocumentHeader' : 'shared.documentRetrievalHeader'
          );
          this.noDocumentMessage.set(this.getNoDocumentMessage(noDoc));
          this.modal.open(this.noDocumentModalId);
        })
      )
      .subscribe();

    this.eventDispatcherService.logout$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(async origin => {
          await this.backToLogin(origin);
        })
      )
      .subscribe();

    this.store
      .select(selectUserState)
      .pipe(
        filter(currentUser => !!(currentUser && currentUser.emailAddress)),
        take(1)
      )
      .subscribe(currentUser => {
        this.dataDogService.setDataDogUserEmail(currentUser.emailAddress ?? '');
      });

    this.keepAlwaysActive();
    this.storePolicyFeedbackMetadata();
  }

  /** Stores policy source systems and UW company codes, read by Qualtrics as survey metadata. */
  private storePolicyFeedbackMetadata() {
    this.store
      .select(selectPolicySummaries)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(policies => !!policies?.length)
      )
      .subscribe(policies => {
        const sourceSystems = Array.from(
          new Set(policies.map(p => p.sourcePath).filter(Boolean))
        ).join(',');
        const uwCompanyCodes = Array.from(
          new Set(policies.map(p => p.amfamCompanyCode).filter(Boolean))
        ).join(',');
        if (sourceSystems)
          localStorage.setItem(LOCAL_STORAGE_KEYS.POLICY_SOURCE_SYSTEM, sourceSystems);
        if (uwCompanyCodes)
          localStorage.setItem(LOCAL_STORAGE_KEYS.UW_COMPANY_CODE, uwCompanyCodes);
      });
  }

  private keepAlwaysActive() {
    this.store
      .select(selectHasFeatureFlag)
      .pipe(
        filter(l => l),
        tap(() => {
          const alwaysActiveEnabled = this.featureService.isEnabled('always_active');
          if (alwaysActiveEnabled) {
            this.sessionHandler = setInterval(() => {
              if (env.environment !== 'prod') {
                console.log('keeping session alive!');
              }
              this.sessionEventsService.triggerUserActivity();
            }, 60000);
          } else {
            if (this.sessionHandler) clearInterval(this.sessionHandler);
          }
        })
      )
      .subscribe();
  }

  private initSalesforceChatWidget() {
    try {
      // Get user data to initialize Salesforce chat
      this.store
        .select(selectUserState)
        .pipe(
          filter(userData => !!userData),
          take(1),
          tap(_userData => {
            // Run outside NgZone so Zone.js does not patch the Salesforce widget's
            // async operations. The widget creates cross-origin iframes that trigger
            // SecurityErrors when Zone.js tries to access their location.href.
            // Without this, those errors propagate to Angular's ErrorHandler and
            // redirect the app to /error.
            this.ngZone.runOutsideAngular(() => {
              // Load the Salesforce chat widget bootstrap script
              const scriptUrl = `${env.salesforceChat.siteUrl}/assets/js/bootstrap.min.js`;
              this.scriptloader
                .loadScript(scriptUrl)
                .then(() => {
                  try {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const { embeddedservice_bootstrap } = this.win
                      .nativeWindow as unknown as SalesforceWindow;
                    if (embeddedservice_bootstrap) {
                      embeddedservice_bootstrap.settings.language = 'en_US';

                      this.win.nativeWindow.addEventListener(
                        'onEmbeddedMessagingReady',
                        async () => {
                          // A reset rebuilds the widget, and the rebuild re-emits this
                          // event. The reset re-verifies the customer itself once the new
                          // widget is up, so events belonging to a widget that is being
                          // replaced are ignored here.
                          if (this.chatSession.isResetting) {
                            return;
                          }

                          // Once per page load, so a conversation restored from whoever
                          // used this device last is never shown to the customer signed in
                          // now. Also rebuilds the widget, which is what makes the chat
                          // launchable again after a clear.
                          if (!this.chatSessionResetStarted) {
                            this.chatSessionResetStarted = true;
                            await this.chatSession.reset();
                            return;
                          }

                          await this.chatSession.applyIdentityToken();
                        }
                      );

                      this.chatIdle.start();
                      this.chatAnalytics.start();

                      this.chatSession.init();
                    } else {
                      this.dataDogService.log(
                        'embeddedservice_bootstrap not found on window object',
                        'error'
                      );
                    }
                  } catch (err) {
                    this.dataDogService.log('Error initializing Embedded Messaging', 'error', {
                      error: err instanceof Error ? err.message : err
                    });
                  }
                })
                .catch(error => {
                  this.dataDogService.log('Error loading Salesforce chat widget script', 'error', {
                    error: error instanceof Error ? error.message : error
                  });
                });
            });
          })
        )
        .subscribe();
    } catch (error) {
      this.dataDogService.log('Error initializing Salesforce chat widget', 'error', {
        error: error instanceof Error ? error.message : error
      });
    }
  }

  private async backToLogin(origin: LogoutOrigin) {
    const experienceId = await firstValueFrom(
      this.store.select(selectSummariesCustomerGroupId).pipe(
        withLatestFrom(this.store.select(selectExperienceId)),
        // Set experience id if logged in as a small partner.
        map(([cId, expId]) => (!MAJOR_PARTNER_IDS.includes(String(cId)) ? expId : null))
      )
    );
    await this.forgerockAuthService.logoutAndRevoke(origin);
    this.forgerockAuthService.logout();
    if (experienceId) {
      window.location.href = window.location.origin + '/' + experienceId;
    } else {
      window.location.href = window.location.origin;
    }
  }

  closeNoDocumentModal() {
    this.modal.close(this.noDocumentModalId);
  }

  private getNoDocumentMessage(noDoc: boolean) {
    return this.copyService.getCopy(
      noDoc ? 'shared.noDocumentMessage' : 'shared.documentRetrievalError'
    );
  }

  ngOnDestroy() {
    this.utilService.log('[Auth] leaving app!');
    this.spinner.start();
    if (this.sessionHandler) clearInterval(this.sessionHandler);
  }
}
